<template>
  <div class="cm-table-wrapper">
    <perfect-scrollbar
      :options="{ suppressScrollY: true }"
    >
      <table class="cm-table">
        <thead>
          <tr>
            <th
              v-for="(col, colKey) in columns"
              :key="colKey"
            >
              <span
                :class="{ 'cm-table__column--sortable': col.sortable }"
                @click="col.sortable ? changeSorting(colKey) : ''"
              >
                {{ col.label }}
                <CmToggleIcon
                  v-if="activeSorting.name === colKey"
                  :isUp="!activeSorting.isDesc"
                />
              </span>
            </th>
          </tr>
        </thead>

        <CmTableStateBlock
          v-if="!isViewState"
          :isLoading="isLoading"
          :isError="isError"
          :isEmpty="isEmpty"
          :colspan="Object.keys(columns).length"
        />

        <template v-else>
          <template v-if="childrenColumns">
            <tbody
              v-for="(row, rowIndex) in paginatedData"
              :key="rowIndex"
            >
              <tr :class="{ 'cm-table__row--with-children': openedRows[rowIndex] }">
                <td
                  v-for="(col, colKey) in columns"
                  :key="colKey"
                >
                  <slot
                    :name="colKey"
                    :row="row"
                    :column="col"
                    :toggleRow="toggleRow"
                    :rowIndex="rowIndex"
                    :isOpen="openedRows[rowIndex]"
                  >
                    {{ row[colKey] }}
                  </slot>
                </td>
              </tr>

              <tr
                v-for="(childrenRow, childrenRowIndex) in row[childrenKey]"
                :key="`${childrenRowIndex}${openedRows[rowIndex]}`"
                v-show="openedRows[rowIndex]"
                class="cm-table__children-row"
              >
                <td
                  v-for="(childrenCol, childrenColKey) in childrenColumns"
                  :key="childrenColKey"
                >
                  <slot :name="childrenColKey" :row="childrenRow" :column="childrenCol">
                    {{ childrenRow[childrenColKey] }}
                  </slot>
                </td>
              </tr>
            </tbody>
          </template>

          <tbody v-else>
            <tr
              v-for="(row, index) in paginatedData"
              :key="index"
            >
              <td
                v-for="(col, colKey) in columns"
                :key="colKey"
              >
                <slot :name="colKey" :row="row" :column="col">
                  {{ row[colKey] }}
                </slot>
              </td>
            </tr>
          </tbody>

          <tfoot>
            <tr>
              <td
                v-for="(col, colKey) in columns"
                :key="colKey"
              >
                {{ col.total === "reduce" ? sumCount(colKey) : col.total || "" }}
              </td>
            </tr>
          </tfoot>
        </template>
      </table>
    </perfect-scrollbar>

    <div v-if="isViewState" class="cm-table__pagination">
      <CmPagination
        :activePage.sync="activePage"
        :countPerChunk="perPageLoc"
        :count="rows.length"
      />

      <label>
        Записей на страницу:
        <v-select
          v-model="perPageLoc"
          :options="perPageOptionsLoc"
          :reduce="label => label.id"
          label="label"
          index="id"
        />
      </label>
    </div>
  </div>
</template>

<script>
import CmTableStateBlock from "@/components/library/CmTableStateBlock.vue";
import CmToggleIcon from "@/components/library/CmToggleIcon.vue";
import CmPagination from "@/components/library/CmPagination.vue";

export default {
  name: "CmTable",

  components: {
    CmToggleIcon,
    CmPagination,
    CmTableStateBlock
  },

  props: {
    isLoading: {
      type: Boolean,
      default: false
    },
    isError: {
      type: Boolean,
      default: false
    },
    columns: {
      type: Object,
      default: () => {}
    },
    rows: {
      type: Array,
      default: () => []
    },
    perPage: {
      type: Number,
      default: 5
    },
    perPageOptions: {
      type: Array,
      default: () => [5, 10, 15, 50],
    },
    childrenKey: {
      type: String,
      default: "children"
    },
    childrenColumns: {
      type: Object
    },
    defaultSorting: {
      type: Object
    }
  },

  data() {
    return {
      perPageLoc: 5,
      openedRows: {},
      activePage: 0,
      activeSorting: {
        name: "",
        isDesc: false
      }
    }
  },

  mounted() {
    this.makeSetup();
  },

  computed: {
    isEmpty() {
      return !this.rows || !this.rows.length;
    },

    isViewState() {
      return !(this.isLoading || this.isError || this.isEmpty);
    },

    sortedData() {
      const { name, isDesc } = this.activeSorting;
      if (!this.columns[name]) return this.rows;

      const sortType = this.columns[name].type || 'string';
      const order = isDesc ? 'desc': 'asc';

      const sortFuncs = {
        numberType: {
          asc: (a, b) => a[name] - b[name],
          desc: (a, b) => b[name] - a[name]
        },
        stringType: {
          asc: (a, b) => a[name].localeCompare(b[name]),
          desc: (a, b) => b[name].localeCompare(a[name])
        }
      };

      return [...this.rows].sort(sortFuncs[sortType + 'Type'][order]);
    },

    paginatedData() {
      const startIndex = this.activePage * this.perPageLoc;
      return this.sortedData.slice(startIndex, startIndex + this.perPageLoc);
    },

    perPageOptionsLoc() {
      const options = this.perPageOptions;
      const res = [];

      options.forEach(op => {
        if (op < this.rows.length) {
          res.push({
            id: op,
            label: op
          });
        }
      });

      res.push({
        id: this.rows.length,
        label: "Все"
      });

      return res;
    },
  },

  watch: {
    rows: {
      deep: true,
      handler(val) {
        // if (this.perPageLoc > val.length) this.perPageLoc = val.length;
        this.activePage = 0;
        this.$forceUpdate();
      }
    },
    perPageLoc() {
      this.activePage = 0;
    }
  },

  methods: {
    makeSetup() {
      if (this.defaultSorting) {
        this.activeSorting = { ...this.defaultSorting };
      }

      this.perPageLoc = this.perPage;
    },

    changeSorting(key) {
      if (this.activeSorting.name === key) {
        this.activeSorting.isDesc = !this.activeSorting.isDesc;
      } else {
        this.activeSorting.name = key;
        this.activeSorting.isDesc = true;
      }
    },

    sumCount(key) {
      const res = this.rows.reduce((total, b) => total + b[key], 0);
      return this.fNum.format(res);
    },

    toggleRow(index) {
      this.openedRows[index] = !this.openedRows[index];
      this.$forceUpdate();
    }
  }
}
</script>

<style lang="scss">
.cm-table {
  width: 100%;

  td, th {
    padding: 0.75em;
    text-align: left;
  }

  thead {
    th {
      vertical-align: baseline;
      font-weight: 600;
      user-select: none;
      span {
        display: flex;
        align-items: center;
        position: relative;
        padding-right: 25px;
        .cm-toggle-icon {
          position: absolute;
          top: 0;
          right: 0;
        }
      }
    }

    .cm-table__column {
      &--sortable {
        cursor: pointer;
        &:hover {
          color: $color_black;
        }
      }
    }
  }

  tbody tr {
    border-top: 0.5px solid #ddd;
    cursor: default;
    &:hover,
    &:active {
      background: rgba(102,204,0,.15);
    }

    td {
      text-align: left;
      vertical-align: baseline;
    }
  }

  // &__row {
  //   &--with-children {
  //   }
  // }

  &__children-row {
    padding-left: 30px;
    background: rgba(232, 232, 232, 0.4);
    td:first-of-type {
      padding-left: 30px;
    }
  }

  tfoot {
    background: #f4f4f4;
    td {
      font-weight: 600;
      padding-bottom: 30px;
    }
  }

  &__spinner {
    display: flex;
    align-items: center;
  }

  &__pagination {
    display: flex;
    margin: 25px 0 40px;
    justify-content: space-between;
    align-items: center;
    position: relative;
    label {
      display: flex;
      align-items: center;
    }
    .v-select {
      width: 100px;
      padding-left: 10px;
    }
    .cm-pagination {
      margin-top: 0;
    }
  }
}
</style>
